/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Context
import { useMaterialUIController, setMiniSidenav } from "context";

function Sidenav({ brand, brandName, routes, ...rest }) {
  // Access global UI controller state (Material Dashboard context)
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;

  // Get current URL location
  const location = useLocation();

  // Extract route name from URL (currently unused but kept if needed later)
  const collapseName = location.pathname.replace("/", "");

  // Close sidenav manually (used in mobile view)
  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Handle responsive behaviour of sidenav
  useEffect(() => {
    function handleMiniSidenav() {
      // Automatically collapse sidenav if screen width < 1200px
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  //check child nested route is active or not
  // Check if any child (or nested child) route is active
  const isChildActive = (collapseRoutes) => {
    return collapseRoutes.some((route) => {
      // If nested collapse exists, check recursively
      if (route.collapse) {
        return isChildActive(route.collapse);
      }

      const fullPath = `${route.layout || ""}${route.route}`;

      // Open parent if:
      //Exact match / current path starts with child path (for nested routes)
      return (
        location.pathname === fullPath ||
        location.pathname.startsWith(fullPath + "/")
      );
    });
  };
  const renderRoutes = (routes) =>
    routes
      .filter((route) => route.show !== false) // Skip hidden routes
      .map((route) => {
        const { type, name, icon, key, route: path, collapse, layout } = route;

        if (type === "collapse") {
          // Parent collapse (has children)
          if (collapse) {
            // Check if any child route is active
            const open = isChildActive(collapse);

            return (
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                // active={open}
                open={open}

                //"active={open}"because that would apply deepBlue color
              >
                {renderRoutes(collapse)}
              </SidenavCollapse>
            );
          }

          // Single clickable route
          return (
            <NavLink
              key={key}
              to={`${layout || ""}${path}`}
              style={{ textDecoration: "none" }}
            >
              {({ isActive }) => (
                <SidenavCollapse name={name} icon={icon} active={isActive} />
              )}
            </NavLink>
          );
        }

        return null;
      });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ miniSidenav }}>
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        {/* Close button (visible only on small screens) */}
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>

        {/* Logo + Brand Name */}
        <MDBox display="flex" alignItems="center">
          {brand && (
            <MDBox component="img" src={brand} alt="Brand" width="10rem" />
          )}

          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography
              component="h6"
              fontWeight="bold"
              sx={(theme) => ({
                color: theme.palette.deepBlue.main,
                fontSize: theme.typography.pxToRem(20),
                letterSpacing: "0.5px",
              })}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      <Divider light />

      {/* ---------------- Navigation List ---------------- */}
      <List>{renderRoutes(routes)}</List>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

Sidenav.defaultProps = {
  brand: "",
};

export default Sidenav;
